import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { UserRoundCog, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useOptions } from "@/hooks/useOptions";
import { userOptions } from "@/lib/users";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { SearchSelect } from "@/components/kit/SearchSelect";

const streamLabel = (s) => `#${s.tenant_seq ?? "?"} — ${s.name || "Stream"}${s.offer_name ? ` — ${s.offer_name}` : ""}`;

// Bulk "Change owner": reassigns the conversion to another buyer AND rebinds it to one of that
// buyer's streams (required) — money is recomputed server-side from the new stream's effective
// rates. Supports Same value for all / Per conversion. The click is never moved.
export function ConversionOwnerDialog({ open, onOpenChange, rows = [], onDone }) {
  const qc = useQueryClient();
  const opts = useOptions();
  const buyerOpts = userOptions(opts.users);
  const [mode, setMode] = useState("uniform");
  const [uniformBuyer, setUniformBuyer] = useState("");
  const [uniformStream, setUniformStream] = useState("");
  const [perRow, setPerRow] = useState({});
  const [streamsByBuyer, setStreamsByBuyer] = useState({});
  const [sendPostback, setSendPostback] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setMode("uniform"); setUniformBuyer(""); setUniformStream(""); setPerRow({}); setSendPostback(false); }
  }, [open]);

  const loadStreams = async (buyerId) => {
    if (!buyerId || streamsByBuyer[buyerId]) return;
    try {
      const { data } = await http.get("/streams", { params: { buyer_id: buyerId, limit: 500 } });
      setStreamsByBuyer((s) => ({ ...s, [buyerId]: data.items || [] }));
    } catch {
      setStreamsByBuyer((s) => ({ ...s, [buyerId]: [] }));
    }
  };

  const streamOpts = (buyerId) => (streamsByBuyer[buyerId] || []).map((s) => ({
    value: s.id, label: streamLabel(s), search: `${s.tenant_seq} ${s.name} ${s.stream_key} ${s.offer_name}`,
  }));

  const pickUniformBuyer = (v) => { setUniformBuyer(v); setUniformStream(""); loadStreams(v); };
  const pickRowBuyer = (rid, v) => { setPerRow((s) => ({ ...s, [rid]: { buyer_id: v, stream_id: "" } })); loadStreams(v); };
  const pickRowStream = (rid, v) => setPerRow((s) => ({ ...s, [rid]: { ...(s[rid] || {}), stream_id: v } }));

  const apply = async () => {
    let items;
    if (mode === "uniform") {
      if (!uniformBuyer) return toast.error("Select a new owner first");
      if (!uniformStream) return toast.error("Select the new owner's stream");
      items = rows.map((r) => ({ id: r.id, buyer_id: uniformBuyer, stream_id: uniformStream }));
    } else {
      const touched = rows.filter((r) => perRow[r.id]?.buyer_id);
      if (!touched.length) return toast.error("Select a new owner first");
      const missing = touched.filter((r) => !perRow[r.id]?.stream_id);
      if (missing.length) return toast.error(`Select a stream for ${missing.length} conversion(s)`);
      items = touched.map((r) => ({ id: r.id, buyer_id: perRow[r.id].buyer_id, stream_id: perRow[r.id].stream_id }));
    }
    setBusy(true);
    try {
      const { data } = await http.post("/conversions/change-owner", { items, send_postback: sendPostback });
      toast.success("Owner changed", { description: `${data.changed} conversion(s) reassigned and repriced${sendPostback ? " · postback sent" : ""}` });
      qc.invalidateQueries({ queryKey: ["resource", "conversions"] });
      qc.invalidateQueries();
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error("Change owner failed", { description: e?.response?.data?.detail || "Try again." });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${mode === "per_row" ? "max-w-3xl" : "max-w-md"} border-white/[0.1] bg-[#141417]`} data-testid="conv-owner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground"><UserRoundCog className="h-4 w-4" /> Change owner</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Reassigns {rows.length} conversion(s) to another buyer and binds them to his stream. Payout/Revenue are
            recomputed from the selected stream's effective rates (incl. personal overrides). The click stays with its
            original owner — both buyers will see it.
          </DialogDescription>
        </DialogHeader>
        <div className="inline-flex rounded-lg border border-white/[0.1] bg-[#0A0A0A] p-0.5 text-xs" data-testid="owner-mode">
          <button onClick={() => setMode("uniform")} data-testid="owner-mode-uniform"
            className={`rounded-md px-3 py-1.5 transition-colors ${mode === "uniform" ? "bg-white text-black" : "text-muted-foreground hover:text-foreground"}`}>Same value for all</button>
          <button onClick={() => setMode("per_row")} data-testid="owner-mode-per-row"
            className={`rounded-md px-3 py-1.5 transition-colors ${mode === "per_row" ? "bg-white text-black" : "text-muted-foreground hover:text-foreground"}`}>Per conversion</button>
        </div>
        {mode === "uniform" ? (
          <div className="space-y-3">
            <div>
              <label className="mb-1.5 block text-xs text-muted-foreground">New owner (Buyer)</label>
              <SearchSelect value={uniformBuyer} onChange={pickUniformBuyer} options={buyerOpts}
                placeholder="Select buyer..." searchPlaceholder="Search buyer..." testId="owner-uniform-buyer" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs text-muted-foreground">New Stream (required)</label>
              <SearchSelect value={uniformStream} onChange={setUniformStream} options={streamOpts(uniformBuyer)}
                disabled={!uniformBuyer}
                placeholder={uniformBuyer ? (streamOpts(uniformBuyer).length ? "Select stream..." : "This buyer has no streams") : "Select buyer first"}
                searchPlaceholder="Search stream..." testId="owner-uniform-stream" widthClass="w-[380px]" />
            </div>
          </div>
        ) : (
          <div className="max-h-[50vh] space-y-1.5 overflow-y-auto pr-1">
            {rows.map((r) => (
              <div key={r.id} className="flex items-center gap-3 rounded-lg border border-white/[0.06] bg-[#0A0A0A] px-3 py-2" data-testid={`owner-row-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs text-foreground">{r.order_id || r.id.slice(0, 8)} · {r.status}</div>
                  <div className="truncate text-[11px] text-muted-foreground">Current: {r.buyer_name || r.buyer_id}</div>
                </div>
                <div className="w-48 shrink-0">
                  <SearchSelect value={perRow[r.id]?.buyer_id || ""} onChange={(v) => pickRowBuyer(r.id, v)}
                    options={buyerOpts} placeholder="Keep current" searchPlaceholder="Search buyer..." testId={`owner-buyer-${r.id}`} />
                </div>
                <div className="w-64 shrink-0">
                  <SearchSelect value={perRow[r.id]?.stream_id || ""} onChange={(v) => pickRowStream(r.id, v)}
                    options={streamOpts(perRow[r.id]?.buyer_id)} disabled={!perRow[r.id]?.buyer_id}
                    placeholder={perRow[r.id]?.buyer_id ? "Select stream..." : "Buyer first"}
                    searchPlaceholder="Search stream..." testId={`owner-stream-${r.id}`} widthClass="w-[320px]" />
                </div>
              </div>
            ))}
          </div>
        )}
        <label className="flex cursor-pointer select-none items-center justify-between gap-2 rounded-lg border border-white/[0.1] bg-[#0A0A0A] p-3 text-sm text-foreground">
          <span>Send Postback <span className="block text-xs text-muted-foreground">Fire the new owner's outbound S2S postbacks after reassignment</span></span>
          <Switch checked={sendPostback} onCheckedChange={setSendPostback} data-testid="owner-send-postback-toggle" />
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-white/[0.12]">Cancel</Button>
          <Button onClick={apply} disabled={busy} data-testid="owner-apply-btn" className="gap-1.5 bg-white text-black hover:bg-white/90">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Change owner
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
